import { CompanyProfile } from 'models';
import { CompanyHelpers } from 'helpers/CompanyHelpers';
import { EventHelpers } from 'helpers/EventHelpers';

import { useCompanyCount } from 'components/company-profile/hooks/use-company-count';
import { useCompanyEvent } from 'components/company-profile/hooks/use-company-event';
import { useProfileProgress } from 'components/company-profile/hooks/use-profile-progress';
import { useCollateral } from 'hooks/use-collateral';
import { DateService } from 'services/DateService';

export type EventReadinessStep = {
    label: string;
    viewLabel: string;
    done: boolean;
    href: string;
};

const useEventReadiness = (company: CompanyProfile) => {
    const { event, booth } = useCompanyEvent(company);
    const { percentage } = useProfileProgress(company);
    const { products } = useCompanyCount(company);
    const { collateral } = useCollateral(event ? company : null);

    const eventIsHappeningNow = event ? EventHelpers.isHappeningNow(event) : false;
    const eventHasEnded = event ? !eventIsHappeningNow && !EventHelpers.isUpcoming(event) : false;

    const steps: EventReadinessStep[] = [
        {
            label: 'Complete your profile',
            viewLabel: 'View profile',
            done: (percentage ?? 0) >= 80,
            href: CompanyHelpers.urls.edit(company.slug),
        },
        {
            label: 'List your products',
            viewLabel: 'View products',
            done: (products?.published ?? 0) > 0,
            href: CompanyHelpers.urls.edit(company.slug, 'products'),
        },
        {
            label: 'Upload collateral',
            viewLabel: 'View collateral',
            done: collateral.length > 0,
            href: `${CompanyHelpers.urls.edit(company.slug, 'collateral')}#collateral`,
        },
    ];

    const nextStep = steps.find((step) => !step.done);

    const headline = event
        ? eventIsHappeningNow
            ? `${event.name} is happening now`
            : eventHasEnded
              ? `${event.name} has ended`
              : `${event.name} starts ${DateService.formatDistanceToNow(event.start)}`
        : null;

    return {
        event,
        booth,
        eventIsHappeningNow,
        eventHasEnded,
        headline,
        steps,
        nextStep,
        doneCount: steps.filter((step) => step.done).length,
    };
};

export { useEventReadiness };
